// LLM 客户端 —— 内置 DeepSeek 服务（经站点 /api/chat 代理，key 在服务端，用户零配置）
// 高级：可配置自定义 OpenAI 兼容端点直连（密钥仅存本机浏览器）
import type { ChatMessage } from './llm-types'

export interface LlmConfig {
  baseUrl: string
  model: string
  apiKey: string
}

const CONFIG_KEY = 'dayi.llm.config'

/** 内置服务端点：站点提供的 /api/chat 代理（同源/跨域均允许，App 内使用绝对地址） */
const API_ORIGIN = 'https://dayi-compass.pages.dev'

export const DEFAULT_LLM_CONFIG: LlmConfig = {
  baseUrl: 'https://api.deepseek.com',
  model: 'deepseek-chat',
  apiKey: '',
}

export const BUILTIN_MODEL = 'deepseek-chat'

export function loadLlmConfig(): LlmConfig {
  try {
    const raw = localStorage.getItem(CONFIG_KEY)
    if (!raw) return { ...DEFAULT_LLM_CONFIG }
    const parsed = JSON.parse(raw) as Partial<LlmConfig>
    return {
      baseUrl: typeof parsed.baseUrl === 'string' && parsed.baseUrl ? parsed.baseUrl : DEFAULT_LLM_CONFIG.baseUrl,
      model: typeof parsed.model === 'string' && parsed.model ? parsed.model : DEFAULT_LLM_CONFIG.model,
      apiKey: typeof parsed.apiKey === 'string' ? parsed.apiKey : '',
    }
  } catch {
    return { ...DEFAULT_LLM_CONFIG }
  }
}

export function saveLlmConfig(config: LlmConfig): void {
  localStorage.setItem(CONFIG_KEY, JSON.stringify(config))
}

/** 内置服务始终可用；自定义 key 仅在用户显式配置时生效 */
export function hasCustomLlmConfig(): boolean {
  return loadLlmConfig().apiKey.trim().length > 0
}

/** 兼容旧调用：内置服务默认可用 */
export function hasLlmConfig(): boolean {
  return true
}

function normalizeBaseUrl(base: string): string {
  const b = base.trim().replace(/\/+$/, '')
  return b.endsWith('/v1') || /\/chat\/completions$/.test(b) ? b : b + '/v1'
}

/** 调用内置服务端代理（DeepSeek，key 在服务端） */
async function chatBuiltin(messages: ChatMessage[], opts: { temperature?: number; maxTokens?: number; timeoutMs?: number }): Promise<string> {
  const system = messages.find((m) => m.role === 'system')?.content ?? ''
  const user = messages.filter((m) => m.role === 'user').map((m) => m.content).join('\n\n')
  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), opts.timeoutMs ?? 120_000)
  try {
    const res = await fetch(`${API_ORIGIN}/api/chat`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ system, user, temperature: opts.temperature ?? 0.7, maxTokens: opts.maxTokens ?? 4000 }),
      signal: controller.signal,
    })
    const data = (await res.json().catch(() => null)) as { content?: string; error?: string } | null
    if (!res.ok) throw new Error(data?.error ? `LLM_PROXY_${res.status}: ${data.error.slice(0, 200)}` : `LLM_PROXY_${res.status}`)
    if (!data?.content) throw new Error('LLM_EMPTY_RESPONSE')
    return data.content
  } finally {
    clearTimeout(timer)
  }
}

/** 调用自定义直连（OpenAI 兼容） */
async function chatDirect(messages: ChatMessage[], opts: { temperature?: number; maxTokens?: number; timeoutMs?: number }): Promise<string> {
  const config = loadLlmConfig()
  if (!config.apiKey.trim()) throw new Error('LLM_API_KEY_MISSING')
  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), opts.timeoutMs ?? 120_000)
  try {
    const res = await fetch(`${normalizeBaseUrl(config.baseUrl)}/chat/completions`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${config.apiKey.trim()}`,
      },
      body: JSON.stringify({
        model: config.model,
        messages,
        temperature: opts.temperature ?? 0.7,
        max_tokens: opts.maxTokens ?? 4000,
        stream: false,
      }),
      signal: controller.signal,
    })
    if (!res.ok) {
      const detail = await res.text().catch(() => '')
      throw new Error(`LLM_HTTP_${res.status}${detail ? ': ' + detail.slice(0, 300) : ''}`)
    }
    const data = (await res.json()) as { choices?: { message?: { content?: string } }[] }
    const content = data.choices?.[0]?.message?.content
    if (!content) throw new Error('LLM_EMPTY_RESPONSE')
    return content
  } finally {
    clearTimeout(timer)
  }
}

export async function chatCompletion(
  messages: ChatMessage[],
  opts: { temperature?: number; maxTokens?: number; timeoutMs?: number } = {},
): Promise<string> {
  return hasCustomLlmConfig() ? chatDirect(messages, opts) : chatBuiltin(messages, opts)
}

/** 请求 JSON 输出：剥离 ```json 围栏与前后杂音后解析 */
export async function chatJson(system: string, user: string, opts: { temperature?: number; maxTokens?: number } = {}): Promise<unknown> {
  const raw = await chatCompletion(
    [
      { role: 'system', content: system },
      { role: 'user', content: user },
    ],
    opts,
  )
  const cleaned = raw.trim().replace(/^```(?:json)?\s*/i, '').replace(/```\s*$/, '')
  const start = cleaned.indexOf('{')
  const end = cleaned.lastIndexOf('}')
  if (start === -1 || end === -1 || end <= start) throw new Error('LLM_BAD_JSON')
  return JSON.parse(cleaned.slice(start, end + 1))
}

/** 测试连通性：内置模式走 /api/chat；自定义模式走直连 */
export async function testLlmConnection(config: LlmConfig): Promise<string> {
  const prev = loadLlmConfig()
  try {
    saveLlmConfig(config)
    const content = await chatCompletion(
      [{ role: 'user', content: 'Reply with exactly: OK' }],
      { maxTokens: 16, timeoutMs: 30_000 },
    )
    return content.includes('OK') ? 'OK' : content.slice(0, 120)
  } finally {
    saveLlmConfig(prev)
  }
}


